import React, { useState } from "react";
import getGeometryIcon from "./utilComponents";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { routing_service_url } from "../api_registry";

export default function NetworkDialog({ project , layers, openPopup, setOpenPopup, onNewLayerCallback}) {
    const [gpFile, setGpFile] = useState(null);
    const [existingRoute, setExistingRoute] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    const appendLog = (message) => {
        setLogs((prev) => [...prev, message]);
    };

    const runRouting = async () => {
        if (!gpFile) {
            appendLog("⚠️ Please select a GP file before running.");
            return;
        }

        setLoading(true);
        setLogs([]);

        try {
            const response = await fetch(routing_service_url, {
                method: "POST",
                headers: {
                    "accept": "*/*",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    project_id: project.id, // you can make this dynamic later
                    gp_layer_id: gpFile,
                    existing_line_layer_id : existingRoute
                }),
            });

            // The API seems to return multiple lines of JSON (stream-like)
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                // Split by newline and process complete JSON chunks
                const parts = buffer.split("\n");
                buffer = parts.pop(); // keep incomplete

                for (const part of parts) {
                    if (!part.trim()) continue;
                    try {
                        const msg = JSON.parse(part);
                        if (msg.message) appendLog(msg.message);
                        if (msg.end) {
                            appendLog("✅ Routing complete!");
                            appendLog(`Layer Created: ${msg.layer_meta.name}`);
                            onNewLayerCallback(msg.layer_meta)
                        }
                    } catch (e) {
                        console.warn("Non-JSON chunk:", part);
                    }
                }
            }

        } catch (error) {
            appendLog(`❌ Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={openPopup}
            onClose={() => !loading && setOpenPopup(false)}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: 1,
                    borderColor: "divider",
                    pb: 1,
                }}
            >
                <Typography variant="h6" fontWeight="600">
                    Create Route Network
                </Typography>
                <Button
                    onClick={() => setOpenPopup(false)}
                    sx={{ minWidth: "auto", color: "text.secondary" }}
                    disabled={loading}
                >
                    <Close />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 , mt : 2}}>
                    {/* GP File Select */}
                    <FormControl fullWidth>
                        <InputLabel>GP File</InputLabel>
                        <Select
                            value={gpFile}
                            onChange={(e) => setGpFile(e.target.value)}
                            label="GP File"
                            disabled={loading}
                            sx={{ borderRadius: 2 }}
                            MenuProps={{
                                PaperProps: { style: { maxHeight: 200 } },
                            }}
                        >
                            {Object.entries(layers).map(([layer_id, layerData]) => (
                                <MenuItem key={layer_id} value={layer_id}>
                                    <Box
                                        sx={{
                                            mr: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            color: "primary.main",
                                        }}
                                    >
                                        {getGeometryIcon(layerData.geometry_types[0])}
                                    </Box>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {layerData.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Existing Route Select */}
                    <FormControl fullWidth>
                        <InputLabel>Existing route</InputLabel>
                        <Select
                            value={existingRoute}
                            onChange={(e) => setExistingRoute(e.target.value)}
                            label="Existing route"
                            disabled={loading}
                            sx={{ borderRadius: 2 }}
                            MenuProps={{
                                PaperProps: { style: { maxHeight: 200 } },
                            }}
                        >
                            {Object.entries(layers).map(([layer_id, layerData]) => (
                                <MenuItem key={layer_id} value={layer_id}>
                                    <Box
                                        sx={{
                                            mr: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            color: "primary.main",
                                        }}
                                    >
                                        {getGeometryIcon(layerData.geometry_types[0])}
                                    </Box>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                        {layerData.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Log Box */}
                    <Box
                        sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 2,
                            p: 2,
                            minHeight: 200,
                            bgcolor: "grey.50",
                            overflowY: "auto",
                            fontFamily: "monospace",
                            fontSize: 13,
                        }}
                    >
                        {logs.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                LOG
                            </Typography>
                        ) : (
                            logs.map((log, idx) => (
                                <Typography
                                    key={idx}
                                    sx={{ whiteSpace: "pre-wrap" }}
                                    color={
                                        log.startsWith("❌")
                                            ? "error.main"
                                            : log.startsWith("✅")
                                            ? "success.main"
                                            : "text.primary"
                                    }
                                >
                                    {log}
                                </Typography>
                            ))
                        )}
                    </Box>

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "flex-end",
                            mt: 1,
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => setOpenPopup(false)}
                            disabled={loading}
                            sx={{ textTransform: "none", px: 4 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={runRouting}
                            disabled={loading}
                            sx={{ textTransform: "none", px: 4 }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress
                                        size={18}
                                        sx={{ mr: 1, color: "white" }}
                                    />
                                    Running...
                                </>
                            ) : (
                                "Run"
                            )}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
